export interface ActivityFeedEmailReplyContext {
    sourceCommunicationId: number;
    sourceSubject: string | null;
    historicalEmail: string;
    allowedEmails: string[];
    preferredEmail: string | null;
    mismatch: boolean;
}

interface EmailRecipient {
    email: string;
}

interface ActivityFeedEmailRecipient extends EmailRecipient {
    valid: boolean;
    validated_email?: {
        is_disposable?: boolean;
    } | null;
    email_sender_suppression_for_current_user?: unknown;
}

export const normalizeEmailAddress = (email: string | null | undefined): string =>
    String(email ?? '')
        .trim()
        .toLowerCase();

export const normalizeAllowedEmailAddresses = (
    emails: ReadonlyArray<string | null | undefined>
): string[] => {
    const normalizedEmails = new Set<string>();

    emails.forEach((email) => {
        const normalizedEmail = normalizeEmailAddress(email);
        if (normalizedEmail) {
            normalizedEmails.add(normalizedEmail);
        }
    });

    return [...normalizedEmails];
};

export const filterEmailRecipientsByAllowedAddresses = <T extends EmailRecipient>(
    recipients: readonly T[],
    allowedEmails: ReadonlyArray<string | null | undefined>
): T[] => {
    const normalizedAllowedEmails = new Set(normalizeAllowedEmailAddresses(allowedEmails));

    return recipients.filter((recipient) =>
        normalizedAllowedEmails.has(normalizeEmailAddress(recipient.email))
    );
};

export const filterActivityFeedEmailRecipients = <T extends ActivityFeedEmailRecipient>(
    recipients: readonly T[],
    allowedEmails: ReadonlyArray<string | null | undefined>
): T[] =>
    filterEmailRecipientsByAllowedAddresses(
        recipients.filter(
            (recipient) =>
                recipient.valid === true &&
                !recipient.validated_email?.is_disposable &&
                !recipient.email_sender_suppression_for_current_user
        ),
        allowedEmails
    );

export const reconcileActivityFeedEmailRecipients = <T extends EmailRecipient>(
    selectedRecipients: readonly EmailRecipient[],
    currentEligibleRecipients: readonly T[]
): T[] => {
    const currentRecipientsByEmail = new Map<string, T>();

    currentEligibleRecipients.forEach((recipient) => {
        const normalizedEmail = normalizeEmailAddress(recipient.email);
        if (normalizedEmail && !currentRecipientsByEmail.has(normalizedEmail)) {
            currentRecipientsByEmail.set(normalizedEmail, recipient);
        }
    });

    const reconciledEmails = new Set<string>();

    return selectedRecipients.reduce<T[]>((recipients, selectedRecipient) => {
        const normalizedEmail = normalizeEmailAddress(selectedRecipient.email);
        const currentRecipient = currentRecipientsByEmail.get(normalizedEmail);
        if (!currentRecipient || reconciledEmails.has(normalizedEmail)) {
            return recipients;
        }

        reconciledEmails.add(normalizedEmail);
        recipients.push(currentRecipient);
        return recipients;
    }, []);
};
